// src/hooks/useInitializeProject.js
import { act, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMasksByIds } from "../services/mask/index.js";
import { v4 as uuidv4 } from "uuid";
import {
  createPrintPages,
  setCurrentObjectProperties,
  applyTheme,
  changeObjectsInAllPages,
  setPageNumber,
  setCalendarSettings,
  setSettings,
  setEditorType,
  setCanvasSize,
  setCanvasScale,
  deSelectSafeArea,
  setTextGroups,
  recordThemeBaseline,
} from "../../../store/slices/canvas.js";
import {
  getMaskIds,
  replaceMaskIdWithPath,
} from "../common-functions/index.js";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { GetThemeById, processProjectPages } from "../services/theme/index.js";
import {
  setEditorPages,
  setThemeId,
  setAllThemes,
  setThemeName,
  setThemeApplied,
  setThemeSmartText,
} from "../../../store/slices/projectSetup.js";
import { EDITOR_TYPES, USER_TYPES } from "../constants/index.js";
import {
  getCanvasSize,
  getActiveEditorType,
  getOrientation,
  getSettings,
} from "../helpers/index.js";
import { useLocation } from "react-router-dom";
/**
 * Custom hook to initialize project details based on URL parameters.
 */
const useSetupTheme = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState(null);
  const [isThemeApplied, setIsThemeApplied] = useState(false);
  const projectSetup = useSelector((state) => state.projectSetup);
  const canvasSize = useSelector(getCanvasSize);
  const activeEditorType = useSelector(getActiveEditorType);
  const activeOrientation = useSelector(getOrientation);
  const editorDetailsExist = useSelector(
    (state) => state.projectSetup.editorDetailsExist
  );
  const settings = useSelector(getSettings);
  const currentCanvasPages = useSelector((state) => state.canvas.present.pages);
  // Tracks the last theme ID that was successfully applied.
  // Used to detect genuine theme switches vs re-renders/re-fires of the same theme.
  const appliedThemeIdRef = useRef(null);
  // Tracks the last pages_c reference that was applied. When projectSetup changes for
  // reasons OTHER than themeDetails (e.g. setAllThemes), pages_c stays the same object
  // reference — we skip re-applying to avoid wiping canvas edits (e.g. spine shifts).
  const appliedPagesCRef = useRef(null);

  const setupTheme = async () => {
    // Capture initial-load status BEFORE replacePages runs (replacePages sets
    // appliedThemeIdRef). True initial load = no theme has been applied yet
    // this session. Used below to decide whether to restore saved-project shape.
    const isInitialLoad = appliedThemeIdRef.current === null;

    // Detect theme switch OR check if it was previously disabled for this project (persists across refresh)
    const projectId = projectSetup.project_id;
    const isSwitchedKey = projectId ? `smart_text_disabled_${projectId}` : null;

    // It's a switch if the ID changed OR if we remember a switch from a previous session (refresh)
    const isThemeSwitch = (appliedThemeIdRef.current &&
                          appliedThemeIdRef.current !== projectSetup.themeDetails?.theme_id) ||
                          (isSwitchedKey && localStorage.getItem(isSwitchedKey) === "true");

    // If it's a new switch, mark it as disabled in localStorage to survive refreshes
    if (isThemeSwitch && isSwitchedKey && appliedThemeIdRef.current) {
      localStorage.setItem(isSwitchedKey, "true");
    }

    let maskIds = [];
    if (projectSetup.themeDetails?.pages_c?.length > 0) {
      maskIds = getMaskIds(projectSetup.themeDetails.pages_c);
    }

    // Resolve mask paths then apply pages
    if (maskIds.length > 0) {
      const maskDetails = await getMasksByIds(maskIds);
      if (maskDetails?.length > 0) {
        replacePages(replaceMaskIdWithPath(projectSetup.themeDetails.pages_c, maskDetails));
      } else {
        replacePages(projectSetup.themeDetails.pages_c);
      }
    } else if (projectSetup.themeDetails?.pages_c?.length > 0) {
      replacePages(projectSetup.themeDetails.pages_c);
    }

    // ── Hydrate Smart Text groups from saved project/theme data ─────────────
    const savedSmartText = projectSetup.themeDetails?.smart_text;

    if (savedSmartText && Object.keys(savedSmartText).length > 0 && !isThemeSwitch) {
      dispatch(setTextGroups(savedSmartText));
    } else if (isThemeSwitch) {
      // Explicitly clear for theme switches to hide the Personalize button
      dispatch(setTextGroups({}));
    }

    // Restore circle/shape setting from the saved project on INITIAL load only.
    // projectSetup.themeDetails.settings is populated by setProjectDetails at init
    // and never refreshed on theme switch. Re-applying it on a theme switch would
    // override the NEW theme's size/shape (already dispatched by ThemesAction)
    // with the previous project's values — user-facing as "size not changing".
    if (isInitialLoad) {
      const savedSettings = projectSetup.themeDetails?.settings;
      if (savedSettings?.shape) {
        dispatch(setSettings({ shape: savedSettings.shape }));
        dispatch(setCanvasSize({
          ...canvasSize,
          shape: savedSettings.shape,
        }));
      }
    }

    // Immediately wipe Undo history and stamp a non-recorded baseline
    dispatch(UndoActionCreators.clearHistory());
    dispatch(recordThemeBaseline({ timestamp: Date.now(), history: false }));
  };
  const getUserType = () => {
    const users = localStorage.getItem("userDetails");
    if (!users) {
      return null;
    }
    const userObj = JSON.parse(users);
    return userObj?.userTypeCode;
  };
  const replacePages = (pages) => {
    const userType = getUserType();

    // when editorDetailsExist means user already saved project, so we dont need to change image
    if (userType === USER_TYPES.CUSTOMER && !editorDetailsExist) {
      // Collect images the customer has already placed in this session (non-empty boxes).
      // On initial theme load the canvas is empty → list is empty → no images carried over (correct).
      // When switching themes the canvas has the customer's placed images → they get carried over.
      const existingImages = [];
      if (currentCanvasPages && currentCanvasPages.length > 0) {
        currentCanvasPages.forEach((page) => {
          page.layout.forEach((layout) => {
            if (!layout) return;
            layout.objects.forEach((obj) => {
              if (obj.type === "img" && obj.url && obj.url.trim() !== "") {
                const largeUrl = obj.urls?.find((u) => u.size === "large");
                if (!largeUrl?.url) return;
                const w = largeUrl?.w || obj.image?.originalWidth || obj.image?.width || 0;
                const h = largeUrl?.h || obj.image?.originalHeight || obj.image?.height || 0;
                const patchedUrls = obj.urls.map((u) =>
                  u.size === "large" ? { ...u, w: w || u.w, h: h || u.h } : u
                );
                existingImages.push({
                  urls: patchedUrls,
                  image_id: obj.image_id,
                  originalWidth: w,
                  originalHeight: h,
                });
              }
            });
          });
        });
      }

      // Clear theme-creator sample images from the new theme pages
      const clearPages = (sourcePages) => sourcePages.map((page) => ({
        ...page,
        layout: page.layout.map((layout) => {
          if (!layout) return layout;
          return {
            ...layout,
            objects: layout.objects.map((obj) =>
              obj.type === "img" && !obj.displaySameImage ? { ...obj, url: "", urls: [] } : obj
            ),
            safeAreaObjects: layout?.safeAreaObjects?.map((obj) =>
              obj.type === "img" && !obj.displaySameImage ? { ...obj, url: "", urls: [] } : obj
            ),
          };
        }),
      }));

      const newPages = clearPages(pages);

      dispatch(setCurrentObjectProperties(null));
      dispatch(applyTheme(newPages));
      if (activeEditorType === EDITOR_TYPES.PRINT) {
        dispatch(
          createPrintPages({
            pageDetails: newPages[0],
            pageCount:
              projectSetup &&
                projectSetup.cartDetails &&
                projectSetup.cartDetails.quantity &&
                projectSetup.cartDetails.quantity !== undefined &&
                projectSetup.cartDetails.quantity !== null &&
                projectSetup.cartDetails.quantity >= 0
                ? parseInt(projectSetup.cartDetails.quantity) - 1
                : 1,
          })
        );
      }
      if (
        (activeEditorType === EDITOR_TYPES.PHOTOBOOK ||
          activeEditorType === EDITOR_TYPES.LAYFLATALBUM) &&
        projectSetup &&
        projectSetup?.cartDetails &&
        projectSetup?.cartDetails?.multiplepaperarray &&
        projectSetup?.cartDetails?.multiplepaperarray.length > 0
      ) {
        let qty = projectSetup?.cartDetails?.multiplepaperarray.reduce(
          (acc, item) => acc + item.quantity,
          0
        );
        // pages will be less than qty / 2 filter pages
        if (
          activeEditorType === EDITOR_TYPES.PHOTOBOOK &&
          qty / 2 < newPages.length - 3
        ) {
          let pages = [];
          // add front cover and pages == qty/2 and back cover
          pages.push(newPages[0]);

          // add first and last half page so condider it as one spread
          pages.push(newPages[1]);

          // add pages in between
          pages.push(...newPages.slice(2, qty / 2 + 1));
          pages.push(newPages[newPages.length - 2]);
          pages.push(newPages[newPages.length - 1]);
          dispatch(applyTheme(pages));
        }
        if (
          activeEditorType === EDITOR_TYPES.PHOTOBOOK &&
          qty / 2 > newPages.length - 3
        ) {
          // extra required spred before back cover and last half page
          let pages = [];
          pages.push(...newPages.slice(0, newPages.length - 2));
          // new blank spread
          for (let i = 0; i < qty / 2 - newPages.length + 3; i++) {
            let randomIndex = Math.floor(Math.random() * (newPages.length - 4));
            let randomPage = newPages.slice(2, newPages.length - 2)[
              randomIndex
            ];
            pages.push(randomPage);
          }
          pages.push(newPages[newPages.length - 2]);
          pages.push(newPages[newPages.length - 1]);
          dispatch(applyTheme(pages));
        }

        if (
          activeEditorType === EDITOR_TYPES.LAYFLATALBUM &&
          !settings?.coverEnabled
        ) {
          if (qty / 2 < newPages.length) {
            let pages = newPages.slice(0, qty / 2);
            dispatch(applyTheme(pages));
          }
          if (qty / 2 > newPages.length) {
            let pages = [...newPages];
            // add extra pages
            for (let i = 0; i < qty / 2 - newPages.length; i++) {
              let randomIndex = Math.floor(
                Math.random() * (newPages.length - 2)
              );
              let randomPage = newPages[randomIndex];
              pages.push(randomPage);
            }
            dispatch(applyTheme(pages));
          }
        } else if (
          activeEditorType === EDITOR_TYPES.LAYFLATALBUM &&
          settings?.coverEnabled &&
          settings?.coverEnabled === true
        ) {
          if (!settings?.showFullCoverSheet) {
            if (qty / 2 < newPages.length - 2) {
              let pages = [];
              // add first cover
              pages.push(newPages[0]);

              // add remaining sheets
              pages.push(...newPages.slice(1, qty / 2 + 1));

              // add last cover
              pages.push(newPages[newPages.length - 1]);
              dispatch(applyTheme(pages));
            }
            if (qty / 2 > newPages.length - 2) {
              let pages = [];
              // add first cover and all sheets
              pages.push(...newPages.slice(0, newPages.length - 2));
              // add extra pages
              for (let i = 0; i < qty / 2 - newPages.length + 3; i++) {
                let randomIndex = Math.floor(
                  Math.random() * (newPages.length - 2)
                );
                // filter pages for random page except first and last cover
                let randomPage = newPages.filter(
                  (page) =>
                    page !== newPages[0] &&
                    page !== newPages[newPages.length - 1]
                )[randomIndex];
                pages.push(randomPage);
              }
              // add last cover
              pages.push(newPages[newPages.length - 1]);
              dispatch(applyTheme(pages));
            }
          }
          if (
            settings?.showFullCoverSheet &&
            settings?.showFullCoverSheet === true
          ) {
            if (qty / 2 < newPages.length - 1) {
              let pages = [];
              // add first cover
              pages.push(newPages[0]);

              // add remaining sheets
              pages.push(...newPages.slice(1, qty / 2 + 1));

              dispatch(applyTheme(pages));
            }

            if (qty / 2 > newPages.length - 1) {
              let pages = [];
              // add first cover and all sheets
              pages.push(...newPages.slice(0, newPages.length - 1));
              // add extra pages
              for (let i = 0; i < qty / 2 - newPages.length + 1; i++) {
                let randomIndex = Math.floor(
                  Math.random() * (newPages.length - 1)
                );
                let randomPage = newPages[randomIndex];
                pages.push(randomPage);
              }
              // add last cover
              pages.push(newPages[newPages.length - 1]);
              dispatch(applyTheme(pages));
            }
          }
        }
      }
      dispatch(deSelectSafeArea());
      dispatch(setPageNumber(0));
      // Refill empty boxes with the customer's previously placed images — same path as PhotosAction
      if (existingImages.length > 0) {
        dispatch(changeObjectsInAllPages({ images: [...existingImages], option: "option1" }));
      }
      appliedThemeIdRef.current = projectSetup.themeDetails?.theme_id;
    } else {
      // Saved project (editorDetailsExist) or admin/employee.
      // For customers: detect a genuine theme switch via the ref — carry images over.
      // For admin/employee or initial load (same theme ID): apply pages as-is.
      const newThemeId = projectSetup.themeDetails?.theme_id;
      const isThemeSwitch =
        userType === USER_TYPES.CUSTOMER &&
        appliedThemeIdRef.current !== null &&
        appliedThemeIdRef.current !== newThemeId;

      if (isThemeSwitch) {
        // Customer switched themes on a saved project — collect their images and carry over
        const existingImages = [];
        if (currentCanvasPages && currentCanvasPages.length > 0) {
          currentCanvasPages.forEach((page) => {
            page.layout.forEach((layout) => {
              if (!layout) return;
              layout.objects.forEach((obj) => {
                if (obj.type === "img" && obj.url && obj.url.trim() !== "") {
                  const largeUrl = obj.urls?.find((u) => u.size === "large");
                  if (!largeUrl?.url) return;
                  const w = largeUrl?.w || obj.image?.originalWidth || obj.image?.width || 0;
                  const h = largeUrl?.h || obj.image?.originalHeight || obj.image?.height || 0;
                  const patchedUrls = obj.urls.map((u) =>
                    u.size === "large" ? { ...u, w: w || u.w, h: h || u.h } : u
                  );
                  existingImages.push({
                    urls: patchedUrls,
                    image_id: obj.image_id,
                    originalWidth: w,
                    originalHeight: h,
                  });
                }
              });
            });
          });
        }
        const clearPages = (sourcePages) => sourcePages.map((page) => ({
          ...page,
          layout: page.layout.map((layout) => {
            if (!layout) return layout;
            return {
              ...layout,
              objects: layout.objects.map((obj) =>
                obj.type === "img" && !obj.displaySameImage ? { ...obj, url: "", urls: [] } : obj
              ),
              safeAreaObjects: layout?.safeAreaObjects?.map((obj) =>
                obj.type === "img" && !obj.displaySameImage ? { ...obj, url: "", urls: [] } : obj
              ),
            };
          }),
        }));
        dispatch(setCurrentObjectProperties(null));
        dispatch(applyTheme(clearPages(pages)));
        dispatch(deSelectSafeArea());
        dispatch(setPageNumber(0));
        if (existingImages.length > 0) {
          dispatch(changeObjectsInAllPages({ images: [...existingImages], option: "option1" }));
        }
      } else {
        // Initial load or admin/employee — apply pages exactly as saved.
        dispatch(setCurrentObjectProperties(null));
        dispatch(applyTheme(pages));
        dispatch(deSelectSafeArea());
        dispatch(setPageNumber(0));
      }
      appliedThemeIdRef.current = newThemeId;
    }
  };
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        if (!projectSetup.themeDetails) {
          return;
        }
        // make sure cart details is not empty object
        if (
          !projectSetup.cartDetails ||
          Object.keys(projectSetup.cartDetails).length === 0
        ) {
          //console.log("cartDetails not available:", projectSetup);
          return;
        }
        if (
          projectSetup.themeDetails &&
          projectSetup.themeDetails.theme_id &&
          projectSetup.themeDetails.pages_c
        ) {
          // Skip re-applying if pages_c reference is unchanged — projectSetup changed
          // for an unrelated reason (e.g. setAllThemes), not because theme pages changed.
          // This prevents wiping canvas edits (like spine object shifts) on every allThemes update.
          if (appliedPagesCRef.current === projectSetup.themeDetails.pages_c) {
            return;
          }
          appliedPagesCRef.current = projectSetup.themeDetails.pages_c;
          await setupTheme();
          setLoading(false);
          setIsThemeApplied(true);
          dispatch(setThemeApplied(true));
        } else {
          // when project run first time, there is no theme data available, so we need to get theme data by theme id
          const theme_id = projectSetup.cartDetails.theme_id;
          if (!theme_id) {
            // No theme — still mark as applied so footer/canvas don't stay in skeleton
            dispatch(setThemeApplied(true));
            return;
            //throw new Error('Missing required parameters: theme_id ');
          }
          const themeData = await GetThemeById(
            theme_id,
            canvasSize,
            activeEditorType,
            activeOrientation
          );
          // const themeData = await GetSampleTheme(theme_id);
          // console.log("themeData:", themeData);
          if (themeData) {
            //   console.log("setEditorPages222:", themeData);
            dispatch(setThemeId(theme_id));

            if (activeEditorType === EDITOR_TYPES.PRINT) {
              let firstPage = themeData.pages_c[0];
              // console.log("count:firstPage:", firstPage);
              //  dispatch(createPrintPages({pageDetails:firstPage,pageCount:2}));
            }

            if (themeData.theme[0].cal_settings) {
              let calSettings = { ...themeData.theme[0].cal_settings };
              if (themeData.theme[0].cal_settings.startMonth) {
                calSettings.startMonth = null;
              }
              if (themeData.theme[0].cal_settings.startYear) {
                calSettings.startYear = null;
              }
              if (calSettings.weeksColumns == null) {
                const pages = Array.isArray(themeData.pages_c) ? themeData.pages_c : [];
                outer: for (const page of pages) {
                  for (const layout of (page.layout || [])) {
                    for (const obj of (layout?.objects || [])) {
                      if ((obj.type === 'calendar' || obj.type === 'multiple-calendar') && obj.calendarSettings?.weeksColumns != null) {
                        calSettings.weeksColumns = obj.calendarSettings.weeksColumns;
                        break outer;
                      }
                    }
                  }
                }
              }
              dispatch(setCalendarSettings(calSettings));
            }
            dispatch(setSettings(themeData.theme[0].settings));
            //  setupTheme();

            // set new size of canvas  only if user is customer
            const user = JSON.parse(localStorage.getItem("userDetails"));
            if (
              themeData?.theme[0]?.width &&
              themeData?.theme[0]?.height &&
              (activeEditorType === EDITOR_TYPES.CANVAS ||
                activeEditorType === EDITOR_TYPES.CUSTOME_PRODUCT) &&
              user &&
              user?.userTypeCode === USER_TYPES.CUSTOMER
            ) {
              dispatch(
                setCanvasSize({
                  width:
                    parseFloat(themeData?.theme[0]?.width) || canvasSize?.width,
                  height:
                    parseFloat(themeData?.theme[0]?.height) ||
                    canvasSize?.height,
                  depth:
                    parseFloat(themeData?.theme[0]?.depth) || canvasSize?.depth,
                  safeMargin:
                    parseFloat(themeData?.theme[0]?.safe_margin) || canvasSize?.safeMargin,
                  bleedMargin:
                    parseFloat(themeData?.theme[0]?.bleed_margin) || canvasSize?.bleedMargin,
                  dpi: parseInt(themeData?.theme[0]?.dpi, 10) || canvasSize?.dpi,
                  shape: themeData?.theme[0]?.settings?.shape || themeData?.theme[0]?.shape || "rectangle",
                })
              );
            }
            // Scale pages and set canvas size from theme dimensions
            const firstTheme = themeData.theme[0];
            const themeWidth = parseFloat(firstTheme.width) || canvasSize?.width;
            const themeHeight = parseFloat(firstTheme.height) || canvasSize?.height;
            const themeDepth = parseFloat(firstTheme.depth) || canvasSize?.depth;

            // For non-CANVAS/CUSTOME_PRODUCT editor types (e.g. PHOTOBOOK, LAYFLATALBUM),
            // always set canvas size from theme dimensions regardless of user type
            if (
              activeEditorType !== EDITOR_TYPES.CANVAS &&
              activeEditorType !== EDITOR_TYPES.CUSTOME_PRODUCT &&
              firstTheme.width &&
              firstTheme.height
            ) {
              // Only update margins if not already handled by the CANVAS/CUSTOME_PRODUCT block above
              if (
                activeEditorType !== EDITOR_TYPES.CANVAS &&
                activeEditorType !== EDITOR_TYPES.CUSTOME_PRODUCT
              ) {
                dispatch(
                  setCanvasSize({
                    ...canvasSize,  // Keep existing width, height, depth
                    safeMargin: parseFloat(themeData?.theme[0]?.safe_margin) || 0,
                    bleedMargin: parseFloat(themeData?.theme[0]?.bleed_margin) || 0,
                    dpi: parseInt(themeData?.theme[0]?.dpi, 10) || canvasSize?.dpi,
                  })
                );
              }
              dispatch(
                setCanvasSize({
                  width: themeWidth,
                  height: themeHeight,
                  depth: themeDepth,
                  safeMargin: parseFloat(firstTheme.safe_margin) || canvasSize?.safeMargin,
                  bleedMargin: parseFloat(firstTheme.bleed_margin) || canvasSize?.bleedMargin,
                  dpi: parseInt(themeData?.theme[0]?.dpi, 10) || canvasSize?.dpi,
                  shape: firstTheme.settings?.shape || firstTheme.shape || canvasSize?.shape || "rectangle",
                })
              );
            }

            // Scale pages from raw compressed data — same approach as admin/size dialog
            let layoutWidth = themeWidth;
            if (activeEditorType === EDITOR_TYPES.PHOTOBOOK) {
              layoutWidth /= 2;
            }
            const scaledPages = processProjectPages(firstTheme.pages_c, layoutWidth, themeHeight);
            dispatch(setEditorPages(scaledPages));

            // Store smart_text on themeDetails so setupTheme() can dispatch
            // setTextGroups AFTER applyTheme() has loaded canvas pages.
            // (Dispatching setTextGroups here would be too early — canvas.pages
            //  is still empty, so the objectId-based re-linking would find nothing.)
            if (firstTheme.smart_text && Object.keys(firstTheme.smart_text).length > 0) {
              dispatch(setThemeSmartText(firstTheme.smart_text));
            }

            // Store all theme sizes for size management
            const normalizedThemes = themeData.theme.map((theme) => ({
              ...theme,
              safeMargin: Number(theme.safeMargin ?? theme.safe_margin) || 0,
              bleedMargin: Number(theme.bleedMargin ?? theme.bleed_margin) || 0,
            }));
            dispatch(setAllThemes(normalizedThemes));

            setLoading(false);
            setIsThemeApplied(true);
            dispatch(setThemeApplied(true));
          } else {
            //throw new Error('Error fetching theme details');
          }
        }
      } catch (err) {
        console.error("Error setting theme:", err);
        setErrorState(err.message); // Set local error state
        setLoading(false); // Mark loading as false even if there's an error
        setIsThemeApplied(false);
      }
    };

    // Only initialize if the URL parameters are available
    initializeTheme();
  }, [projectSetup]);

  useEffect(() => {
    const initTheme = async () => {
      try {
        // Get cart order id
        const urlParams = new URLSearchParams(location.search);

        const token = urlParams.get("u_id"); // get token  as this used for admin to create
        const theme_id = urlParams.get("t_id"); // get token  as this used for admin to create

        // console.log("theme_id param:", theme_id);
        // if project_id or token is missing then return
        if (!theme_id && !token) {
          return;
        }

        const themeData = await GetThemeById(
          theme_id,
          canvasSize,
          activeEditorType,
          activeOrientation
        );

        if (themeData) {
          //when load theme using theme id in url.
          setupThemeFromURL(themeData, theme_id);
          setLoading(false);
          setIsThemeApplied(true);
          dispatch(setThemeApplied(true));
        } else {
          // No theme data (e.g. admin with u_id but no t_id) — still clear skeleton
          dispatch(setThemeApplied(true));
        }
      } catch (err) {
        // On error still clear skeleton so editor isn't stuck
        dispatch(setThemeApplied(true));
      }
    };

    initTheme();
  }, [location.search]);

  const setupThemeFromURL = (themeData, themeid) => {
    if (!themeData && !themeData.pages_c) {
      return;
    }
    dispatch(setCalendarSettings(null));

    const sizeToCheck = `${canvasSize.width}x${canvasSize.height}`;
    const sizeExist = themeData.theme.find((item) => item.size === sizeToCheck);
    if (sizeExist) {
      dispatch(
        setCanvasSize({
          width: parseFloat(sizeExist.width),
          height: parseFloat(sizeExist.height),
          depth: parseFloat(sizeExist.depth),
          safeMargin: parseFloat(sizeExist.safe_margin),
          bleedMargin: parseFloat(sizeExist.bleed_margin),
          dpi: parseInt(sizeExist.dpi, 10) || canvasSize?.dpi,
          shape: sizeExist.settings?.shape || sizeExist.shape || "rectangle",
        })
      );
    }

    const normalizedThemes = themeData.theme.map((theme) => ({
      ...theme,
      safeMargin: Number(theme.safeMargin ?? theme.safe_margin) || 0,
      bleedMargin: Number(theme.bleedMargin ?? theme.bleed_margin) || 0,
    }));

    dispatch(setAllThemes(normalizedThemes));

    dispatch(setEditorType(themeData.editor_type));

    //  console.log("themeData.calendar settings:", themeData.theme[0].cal_settings);
    if (themeData.theme[0].cal_settings) {
      // lets remove month and year from calendar settings
      // create clone of cal_settings
      let calSettings = { ...themeData.theme[0].cal_settings };
      if (themeData.theme[0].cal_settings.startMonth) {
        calSettings.startMonth = null;
      }
      if (themeData.theme[0].cal_settings.startYear) {
        calSettings.startYear = null;
      }
      if (calSettings.weeksColumns == null) {
        const pages = Array.isArray(themeData.pages_c) ? themeData.pages_c : [];
        outer: for (const page of pages) {
          for (const layout of (page.layout || [])) {
            for (const obj of (layout?.objects || [])) {
              if ((obj.type === 'calendar' || obj.type === 'multiple-calendar') && obj.calendarSettings?.weeksColumns != null) {
                calSettings.weeksColumns = obj.calendarSettings.weeksColumns;
                break outer;
              }
            }
          }
        }
      }
      dispatch(setCalendarSettings(calSettings));
    }
    dispatch(setSettings(themeData.theme[0].settings));
    dispatch(setThemeId(themeid));
    dispatch(setThemeName(themeData.name));

    // get first theme and set it as active theme

    const theme = themeData.theme[0];
    const width = theme.width;
    const height = theme.height;

    if (themeData.editor_type === EDITOR_TYPES.PHOTOBOOK) {
      width /= 2;
    }
    // Note: LAYFLATALBUM saves layouts at full canvasSize.width, so no halving needed

    // No setTimeout needed here: this is the URL/admin initial-load path where
    // isThemeApplied flips false→true right after this function returns.
    // Canvas.jsx:1597 fires updateCanvasSize IMMEDIATELY on that flip (no debounce),
    // so the viewport adjusts to the new canvasSize before pages render.
    // (The 500ms debounced effect at Canvas.jsx:1603 still runs as a second pass.)
    dispatch(
      setCanvasSize({
        width: parseFloat(width),
        height: parseFloat(height),
        depth: parseFloat(theme.depth),
        safeMargin: parseFloat(theme.safe_margin),
        bleedMargin: parseFloat(theme.bleed_margin),
        dpi: parseInt(theme.dpi, 10) || canvasSize?.dpi,
        shape: theme.settings?.shape || theme.shape || "rectangle",
      })
    );

    const pages = processProjectPages(theme.pages_c, width, height);
    dispatch(setEditorPages(pages));

    // Store smart_text on themeDetails so setupTheme() can dispatch
    // setTextGroups AFTER applyTheme() loads canvas pages (correct timing).
    if (theme.smart_text && Object.keys(theme.smart_text).length > 0) {
      dispatch(setThemeSmartText(theme.smart_text));
    }

    // Wipe the Undo history so that the user cannot accidentally "Undo" the entire theme initialization!
    dispatch(UndoActionCreators.clearHistory());
    dispatch(recordThemeBaseline({ timestamp: Date.now(), history: false }));
  };

  // Return loading state, error, and current project state
  return { loading, error, isThemeApplied };
};

export default useSetupTheme;
